import { useState, useEffect, useContext, useMemo } from 'react';
import { AuthContext } from '../context/authContext';
import { Input } from "../components/ui";

const ROLES = [
    { key: 'todos', label: 'Todos' },
    { key: 'admin', label: 'Admins' },
    { key: 'editor_provincial', label: 'Provinciales' },
    { key: 'editor_municipio', label: 'Municipales' },
    { key: 'editor_entidad', label: 'Entidades' },
];

const roleBadgeStyles = {
    admin: 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200',
    editor_provincial: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200',
    editor_municipio: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200',
    editor_entidad: 'bg-amber-100 text-amber-800 dark:bg-amber-900 dark:text-amber-200',
};

const roleLabels = {
    admin: 'Admin',
    editor_provincial: 'Editor Provincial',
    editor_municipio: 'Editor Municipal',
    editor_entidad: 'Editor Entidad',
};

export const AdminUsersPage = () => {
    const { token, userId } = useContext(AuthContext);
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [message, setMessage] = useState(null);
    const [filter, setFilter] = useState('todos');
    const [search, setSearch] = useState('');
    const [actionInProgress, setActionInProgress] = useState(null);

    const fetchUsers = async () => {
        setLoading(true);
        try {
            const response = await fetch('/api/authentication/admin/users', {
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });

            if (!response.ok) throw new Error('Error al cargar usuarios');

            const data = await response.json();
            setUsers(data);
        } catch (error) {
            setMessage(error.message);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchUsers();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleToggleActivo = async (user) => {
        const nuevoEstado = !user.activo;
        const accion = nuevoEstado ? 'reactivar' : 'dar de baja';

        if (!confirm(`¿Está seguro de ${accion} al usuario ${user.name} (${user.email})?`)) {
            return;
        }

        setActionInProgress(user.id);
        setMessage(null);
        try {
            const response = await fetch(`/api/authentication/admin/users/${user.id}/activo`, {
                method: 'PATCH',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({ activo: nuevoEstado })
            });

            const data = await response.json();

            if (!response.ok) throw new Error(data.error || `Error al ${accion} el usuario`);

            setMessage(data.message);
            fetchUsers();
        } catch (error) {
            setMessage(error.message);
        } finally {
            setActionInProgress(null);
        }
    };

    const counts = useMemo(() => {
        return users.reduce((acc, u) => {
            acc[u.role] = (acc[u.role] || 0) + 1;
            acc.todos = (acc.todos || 0) + 1;
            return acc;
        }, {});
    }, [users]);

    const filteredUsers = useMemo(() => {
        let result = users;
        if (filter !== 'todos') {
            result = result.filter(u => u.role === filter);
        }
        if (search.trim()) {
            const q = search.trim().toLowerCase();
            result = result.filter(u =>
                u.name?.toLowerCase().includes(q) ||
                u.email?.toLowerCase().includes(q) ||
                u.user_municipio?.toLowerCase().includes(q) ||
                String(u.entidad_id_red || '').toLowerCase().includes(q)
            );
        }
        return result;
    }, [users, filter, search]);

    const formatDate = (dateString) => {
        if (!dateString) return '-';
        return new Date(dateString).toLocaleDateString('es-AR', {
            day: '2-digit', month: '2-digit', year: 'numeric'
        });
    };

    return (
        <div className="min-h-screen bg-gray-50 dark:bg-gray-900 py-8">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
                    <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
                        Gestión de Usuarios
                    </h1>
                    <div className="w-full sm:w-72">
                        <Input
                            type="text"
                            placeholder="Buscar por nombre, email o municipio..."
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                        />
                    </div>
                </div>

                {message && (
                    <div className={`mb-4 rounded-md p-4 ${message.includes('Error') || message.includes('error') ? 'bg-red-50 dark:bg-red-900/30' : 'bg-green-50 dark:bg-green-900/30'}`}>
                        <p className={`text-sm font-medium ${message.includes('Error') || message.includes('error') ? 'text-red-800 dark:text-red-200' : 'text-green-800 dark:text-green-200'}`}>
                            {message}
                        </p>
                    </div>
                )}

                {/* Filter tabs por rol */}
                <div className="mb-6">
                    <div className="border-b border-gray-200 dark:border-gray-700">
                        <nav className="-mb-px flex space-x-8 overflow-x-auto">
                            {ROLES.map((r) => (
                                <button
                                    key={r.key}
                                    onClick={() => setFilter(r.key)}
                                    className={`
                                        py-2 px-1 border-b-2 font-medium text-sm whitespace-nowrap
                                        ${filter === r.key
                                            ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                                            : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300'}
                                    `}
                                >
                                    {r.label}
                                    {counts[r.key] !== undefined && (
                                        <span className="ml-2 inline-flex items-center justify-center px-2 py-0.5 text-xs font-medium rounded-full bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300">
                                            {counts[r.key]}
                                        </span>
                                    )}
                                </button>
                            ))}
                        </nav>
                    </div>
                </div>

                {/* Tabla de usuarios */}
                <div className="bg-white dark:bg-gray-800 shadow overflow-hidden sm:rounded-lg">
                    {loading ? (
                        <div className="flex items-center justify-center py-12">
                            <svg className="animate-spin h-8 w-8 text-blue-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                            </svg>
                        </div>
                    ) : filteredUsers.length === 0 ? (
                        <p className="text-center text-gray-500 dark:text-gray-400 py-12">
                            No hay usuarios para mostrar.
                        </p>
                    ) : (
                        <div className="overflow-x-auto">
                            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                                <thead className="bg-gray-50 dark:bg-gray-700">
                                    <tr>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Usuario</th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Rol</th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Municipio / Entidad</th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Alta</th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Estado</th>
                                        <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Acciones</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                                    {filteredUsers.map((u) => (
                                        <tr key={u.id} className={`${!u.activo ? 'opacity-60' : ''} hover:bg-gray-50 dark:hover:bg-gray-700/50`}>
                                            <td className="px-6 py-4">
                                                <div className="text-sm font-medium text-gray-900 dark:text-white">{u.name}</div>
                                                <div className="text-sm text-gray-500 dark:text-gray-400">{u.email}</div>
                                            </td>
                                            <td className="px-6 py-4">
                                                <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${roleBadgeStyles[u.role] || 'bg-gray-100 text-gray-800 dark:bg-gray-600 dark:text-gray-200'}`}>
                                                    {roleLabels[u.role] || u.role}
                                                </span>
                                                {u.is_especial && (
                                                    <span className="ml-1 inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-cyan-100 text-cyan-800 dark:bg-cyan-900 dark:text-cyan-200">
                                                        Subse Hábitat
                                                    </span>
                                                )}
                                            </td>
                                            <td className="px-6 py-4 text-sm text-gray-500 dark:text-gray-400">
                                                {u.user_municipio
                                                    ? u.user_municipio
                                                    : (u.entidad_id_red ? `ID_RED: ${u.entidad_id_red}` : '-')}
                                            </td>
                                            <td className="px-6 py-4 text-sm text-gray-500 dark:text-gray-400">
                                                {formatDate(u.created_at)}
                                            </td>
                                            <td className="px-6 py-4">
                                                {u.activo ? (
                                                    <span className="inline-flex items-center px-2.5 py-0.5 text-xs font-medium rounded-full bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200">
                                                        Activo
                                                    </span>
                                                ) : (
                                                    <span className="inline-flex items-center px-2.5 py-0.5 text-xs font-medium rounded-full bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200">
                                                        De baja
                                                    </span>
                                                )}
                                            </td>
                                            <td className="px-6 py-4 text-right">
                                                {(() => {
                                                    const canReactivate = !u.activo;
                                                    const canDeactivate = u.activo && u.role !== 'admin' && Number(u.id) !== Number(userId);
                                                    if (!canReactivate && !canDeactivate) return null;
                                                    return (
                                                        <button
                                                            onClick={() => handleToggleActivo(u)}
                                                            disabled={actionInProgress === u.id}
                                                            className={`
                                                                px-3 py-1.5 text-sm font-medium rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed
                                                                ${canDeactivate
                                                                    ? 'text-red-700 bg-red-50 hover:bg-red-100 dark:bg-red-900/40 dark:text-red-300 dark:hover:bg-red-900/60'
                                                                    : 'text-green-700 bg-green-50 hover:bg-green-100 dark:bg-green-900/40 dark:text-green-300 dark:hover:bg-green-900/60'}
                                                            `}
                                                        >
                                                            {actionInProgress === u.id ? '...' : (canDeactivate ? 'Dar de baja' : 'Reactivar')}
                                                        </button>
                                                    );
                                                })()}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};
